import { Link } from "react-router-dom";
import { useTranslation } from "../../i18n";

import oebbLogo from "../newApp/assets/logos/oebb.png";
import fellbachLogo from "../newApp/assets/logos/fellbachgo.png";
import opgLogo from "../newApp/assets/logos/opg.png";
import allgaeuLogo from "../newApp/assets/logos/allgaeutissimo.png";

const PARTNER_LOGOS = [
  ["oebb", "ÖBB"],
  ["fellbachgo", "Fellbach.go!"],
  ["koch-partner", "Koch & Partner"],
  ["allgaeutissimo", "Allgäutissimo Bikeshop"],
  ["velodepo", "VELODEPO"],
  ["opg", "OPG"],
  ["greenguard", "Greenguard"],
  ["emz", "emz"],
  ["hochschule-ansbach", "Hochschule Ansbach"],
  ["gablitz", "Gablitz"],
  ["mauerbach", "Mauerbach"],
  ["purkersdorf", "Stadtgemeinde Purkersdorf"],
  ["w5ww", "Wir 5 im Wienerwald"],
];

/** Per-case presentation: logo, its height, avatar colour, stat count, layout side. */
const CASES = [
  {
    logo: oebbLogo,
    logoHeight: 28,
    avatarClass: "bg-juhuu-soft text-juhuu",
    avatarTextSize: "text-base",
    statCount: 3,
    flipped: false,
    tinted: false,
  },
  {
    logo: fellbachLogo,
    logoHeight: 24,
    avatarClass: "bg-ink text-white",
    avatarTextSize: "text-[.72rem]",
    statCount: 3,
    flipped: true,
    tinted: true,
    hasTransit: true,
  },
  {
    logo: opgLogo,
    logoHeight: 30,
    avatarClass: "text-white",
    avatarStyle: { background: "#004f9f" },
    avatarTextSize: "text-[.72rem]",
    statCount: 2,
    flipped: false,
    tinted: false,
  },
  {
    logo: allgaeuLogo,
    logoHeight: 30,
    avatarClass: "bg-ink text-white",
    avatarTextSize: "text-base",
    statCount: 3,
    flipped: true,
    tinted: true,
  },
];

const QuoteMark = () => (
  <svg
    width="34"
    height="34"
    viewBox="0 0 24 24"
    fill="#e6dbff"
    aria-hidden="true"
  >
    <path d="M9 11H5a1 1 0 0 1-1-1V7a4 4 0 0 1 4-4h0v2a2 2 0 0 0-2 2v1h3a1 1 0 0 1 1 1v1a1 1 0 0 1-1 1Zm10 0h-4a1 1 0 0 1-1-1V7a4 4 0 0 1 4-4h0v2a2 2 0 0 0-2 2v1h3a1 1 0 0 1 1 1v1a1 1 0 0 1-1 1Z" />
  </svg>
);

export default function NewReferencesRoute() {
  const { t } = useTranslation();

  const caseStats = (caseIndex: number, count: number) => (
    <div className="flex flex-wrap gap-x-7 gap-y-4">
      {Array.from({ length: count }, (_, i) => (
        <div key={i}>
          <div className="text-[1.6rem] font-extrabold leading-none tracking-[-0.04em] text-ink">
            {t(`routes.references.cases.${caseIndex}.stats.${i}.value`)}
          </div>
          <div className="mt-2 text-[.78rem] font-bold uppercase tracking-[.1em] text-light">
            {t(`routes.references.cases.${caseIndex}.stats.${i}.label`)}
          </div>
        </div>
      ))}
    </div>
  );

  const caseCopy = (i: number) => (
    <div>
      <div className="mb-4 flex items-center gap-3.5">
        <div className="grid h-12 min-w-[48px] place-items-center rounded-xl border border-line bg-white px-3">
          <img
            src={CASES[i].logo}
            alt={t(`routes.references.cases.${i}.org`)}
            style={{ height: CASES[i].logoHeight }}
            className="block w-auto"
          />
        </div>
        <div>
          <div className="font-extrabold text-ink">
            {t(`routes.references.cases.${i}.org`)}
          </div>
          <div className="text-[.85rem] text-muted">
            {t(`routes.references.cases.${i}.sub`)}
          </div>
        </div>
      </div>
      <h2 className="text-[clamp(1.5rem,2.3vw,2.1rem)] font-extrabold leading-[1.08] tracking-[-0.03em] text-ink">
        {t(`routes.references.cases.${i}.titleLine1`)}
        <br />
        {t(`routes.references.cases.${i}.titleLine2`)}
      </h2>
      <p className="my-5 leading-[1.65] text-muted">
        {t(`routes.references.cases.${i}.description`)}
      </p>
      {caseStats(i, CASES[i].statCount)}
    </div>
  );

  const quoteCard = (i: number) => (
    <div className="rounded-2xl border border-line bg-gradient-to-br from-white to-bg-alt p-[clamp(1.7rem,3vw,2.6rem)] shadow-sm">
      <QuoteMark />
      <p className="my-5 text-[1.1rem] font-semibold leading-[1.6] text-ink">
        {t(`routes.references.cases.${i}.quote`)}
      </p>

      {CASES[i].hasTransit && (
        <div className="mb-5 flex items-center gap-3 rounded-[14px] border border-juhuu-soft-2 bg-juhuu-soft px-4 py-3.5">
          <div className="grid h-[34px] w-[34px] flex-shrink-0 place-items-center rounded-[9px] bg-white text-juhuu">
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              className="h-[18px] w-[18px]"
            >
              <rect x="4" y="3" width="16" height="14" rx="2" />
              <path d="M4 11h16M8 21l2-4M16 21l-2-4" />
            </svg>
          </div>
          <div className="text-[.84rem] text-juhuu-deep">
            <strong>{t(`routes.references.cases.${i}.transitLabel`)}</strong>
            <br />
            {t(`routes.references.cases.${i}.transitText`)}
          </div>
        </div>
      )}

      <div className="flex items-center gap-2.5 border-t border-line pt-5">
        <div
          className={`grid h-[38px] w-[38px] flex-shrink-0 place-items-center rounded-full font-extrabold ${CASES[i].avatarClass} ${CASES[i].avatarTextSize}`}
          style={CASES[i].avatarStyle}
        >
          {t(`routes.references.cases.${i}.authorInitials`)}
        </div>
        <div>
          <div className="text-[.9rem] font-bold text-ink">
            {t(`routes.references.cases.${i}.authorRole`)}
          </div>
          <div className="text-[.8rem] text-muted">
            {t(`routes.references.cases.${i}.authorOrg`)}
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen overflow-x-hidden bg-white">
      <main>
        {/* ── Page hero ────────────────────────────────────── */}
        <section className="relative px-4 md:px-8 pt-32 pb-[clamp(2.5rem,5vw,4rem)] text-center">
          <div className="pointer-events-none absolute -top-28 left-1/2 h-[440px] w-[440px] -translate-x-1/2 rounded-full bg-juhuu-glow blur-[90px]" />
          <div className="relative mx-auto max-w-7xl">
            <p className="text-[.78rem] font-bold uppercase tracking-[.16em] text-juhuu">
              {t("routes.references.kicker")}
            </p>
            <h1 className="mt-4 text-[clamp(2.4rem,4.6vw,3.8rem)] font-extrabold leading-[1.02] tracking-[-0.035em] text-ink">
              {t("routes.references.heroTitleLine1")}
              <br />
              {t("routes.references.heroTitleLine2")}
            </h1>
            <p className="mx-auto mt-5 max-w-[620px] text-[clamp(1.05rem,0.7vw+0.95rem,1.2rem)] font-medium leading-[1.65] text-muted">
              {t("routes.references.heroDescription")}
            </p>
          </div>
        </section>

        {/* ── Logo marquee (46px logos, 35s scroll) ────────── */}
        <section className="px-4 md:px-8 pb-14">
          <div className="mx-auto max-w-7xl">
            <div className="w-full overflow-hidden [mask-image:linear-gradient(90deg,transparent,#000_10%,#000_90%,transparent)]">
              <div className="flex w-max items-center gap-[clamp(2rem,5vw,4rem)] [animation:marquee_35s_linear_infinite] hover:[animation-play-state:paused]">
                {[...PARTNER_LOGOS, ...PARTNER_LOGOS].map(([slug, name], i) => (
                  <span key={i} className="inline-flex flex-shrink-0">
                    <img
                      src={require(`../newApp/assets/logos/${slug}.png`)}
                      alt={name}
                      loading="lazy"
                      className="h-[46px] w-auto max-w-[196px] object-contain opacity-55 transition-opacity hover:opacity-100"
                    />
                  </span>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* ── Stats strip ──────────────────────────────────── */}
        <section className="px-4 md:px-8 pb-14">
          <div className="mx-auto max-w-7xl">
            <div className="grid gap-8 rounded-2xl border border-line bg-white px-4 py-8 text-center shadow-sm sm:grid-cols-2 lg:grid-cols-4">
              {[0, 1, 2, 3].map((i) => (
                <div key={i}>
                  <div className="text-[clamp(2.1rem,3.4vw,3rem)] font-extrabold leading-none tracking-[-0.04em] text-juhuu">
                    {t(`routes.references.stats.${i}.value`)}
                  </div>
                  <div className="mt-2 text-[.78rem] font-bold uppercase tracking-[.1em] text-light">
                    {t(`routes.references.stats.${i}.label`)}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── Case studies ─────────────────────────────────── */}
        {CASES.map((c, i) => (
          <section
            key={i}
            className={`px-4 md:px-8 py-20 ${c.tinted ? "bg-bg-alt" : ""}`}
          >
            <div className="mx-auto grid max-w-7xl items-center gap-12 lg:grid-cols-2">
              {c.flipped ? (
                <>
                  <div className="order-2 lg:order-1">{quoteCard(i)}</div>
                  <div className="order-1 lg:order-2">{caseCopy(i)}</div>
                </>
              ) : (
                <>
                  {caseCopy(i)}
                  {quoteCard(i)}
                </>
              )}
            </div>
          </section>
        ))}

        {/* ── More operators ───────────────────────────────── */}
        <section className="px-4 md:px-8 py-20">
          <div className="mx-auto max-w-7xl">
            <div className="mx-auto mb-10 max-w-[560px] text-center">
              <p className="text-[.78rem] font-bold uppercase tracking-[.16em] text-juhuu">
                {t("routes.references.moreKicker")}
              </p>
              <h2 className="mt-4 text-[clamp(1.8rem,3vw,2.6rem)] font-extrabold leading-[1.08] tracking-[-0.03em] text-ink">
                {t("routes.references.moreTitle")}
              </h2>
            </div>

            <div className="grid gap-6 md:grid-cols-3">
              {[0, 1, 2].map((i) => (
                <div
                  key={i}
                  className="rounded-2xl border border-line bg-white p-7 shadow-sm transition-all hover:-translate-y-1 hover:shadow-lg"
                >
                  <div className="mb-4 flex items-center gap-2.5">
                    <div className="grid h-[42px] w-[42px] place-items-center rounded-[11px] bg-juhuu-soft text-juhuu">
                      <svg
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        className="h-5 w-5"
                      >
                        <path d="M3 21h18M5 21V7l5-4 5 4v14M9 9h2M9 13h2" />
                      </svg>
                    </div>
                    <div>
                      <div className="text-[.98rem] font-extrabold text-ink">
                        {t(`routes.references.more.${i}.name`)}
                      </div>
                      <div className="text-[.78rem] text-muted">
                        {t(`routes.references.more.${i}.type`)}
                      </div>
                    </div>
                  </div>
                  <p className="text-[.9rem] leading-[1.6] text-muted">
                    {t(`routes.references.more.${i}.description`)}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── Closing CTA ──────────────────────────────────── */}
        <section className="bg-bg-tint px-4 md:px-8 py-20 text-center">
          <div className="mx-auto max-w-[620px]">
            <h2 className="text-[clamp(1.8rem,3vw,2.6rem)] font-extrabold leading-[1.08] tracking-[-0.03em] text-ink">
              {t("routes.references.finalCtaTitle")}
            </h2>
            <p className="mx-auto my-6 max-w-[460px] text-[clamp(1.05rem,0.7vw+0.95rem,1.2rem)] font-medium leading-[1.65] text-muted">
              {t("routes.references.finalCtaDescription")}
            </p>
            <Link
              to="/meeting"
              className="inline-flex items-center rounded-full bg-juhuu px-7 py-3.5 font-semibold text-white shadow-[0_14px_30px_-10px_rgba(109,22,250,.55)] transition-all hover:-translate-y-0.5 hover:bg-juhuu-ink"
            >
              {t("routes.references.finalCtaButton")}
            </Link>
          </div>
        </section>
      </main>
    </div>
  );
}
